import type { ReactNode } from 'react';
import { Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { getHelpHtml } from '../../utils/help/help-content';
import type {
  CompetitorInsight,
  ProjectVisibilityOwnBrand,
  ProjectVisibilityTopBrand,
} from '../../types';
import { DASHBOARD_PLOT_HEIGHT } from '../../utils/competition-chart';

import { DashboardEmptyNote } from './DashboardEmptyNote';
import { ShareOfVoiceChart } from './ShareOfVoiceChart';
import { useTrackerAiPath } from '../../utils/paths';

export function OwnBrandOverviewCard({
  ownBrand,
  competitors,
  trackedCompetitors,
  footer,
  plotHeight,
  hideHeader,
  title: titleOverride,
  rangeHasExecutions = false,
  hasBrandAliases = false,
  focusKey,
}: {
  ownBrand: Pick<ProjectVisibilityOwnBrand, 'canonicalName' | 'shareOfVoice'> | null;
  competitors: Pick<ProjectVisibilityTopBrand, 'knownBrandId' | 'canonicalName' | 'domain' | 'shareOfVoice'>[];
  trackedCompetitors: CompetitorInsight[];
  footer?: ReactNode;
  plotHeight?: number;
  hideHeader?: boolean;
  title?: ReactNode;
  /** False when the selected range has no completed runs. */
  rangeHasExecutions?: boolean;
  hasBrandAliases?: boolean;
  /** When set, this brand is bold instead of the own brand (domain filter). */
  focusKey?: string | null;
}) {
  const toTrackerAi = useTrackerAiPath();
  const title = titleOverride ?? (
    <span className="d-inline-flex align-items-center gap-1">
      سهم از دیده‌شدن (Share of Voice)
      <HelpTooltip html={getHelpHtml('share_of_voice')} />
    </span>
  );

  if (!ownBrand?.canonicalName && !focusKey) {
    return (
      <Card className="h-100 shadow-sm d-flex flex-column">
        {hideHeader ? null : (
          <FalconCardHeader light titleTag="h6" className="py-3" title={title} />
        )}
        <Card.Body className="d-flex flex-column flex-grow-1 p-0">
          <DashboardEmptyNote minHeight={plotHeight ?? DASHBOARD_PLOT_HEIGHT}>
            {!rangeHasExecutions ? (
              'در این بازه پاسخی از مدل‌های AI ثبت نشده.'
            ) : hasBrandAliases ? (
              'برند شما در پاسخ‌های این بازه دیده نشده.'
            ) : (
              <>
                نام‌های مستعار برند را در{' '}
                <Link to={toTrackerAi('settings')} className="fw-semibold">
                  تنظیمات
                </Link>{' '}
                اضافه کنید تا برندتان در پاسخ‌ها شناسایی شود.
              </>
            )}
          </DashboardEmptyNote>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100 shadow-sm d-flex flex-column">
      {hideHeader ? null : (
        <FalconCardHeader light titleTag="h6" className="py-3" title={title} />
      )}
      <Card.Body className="pt-3 d-flex flex-column flex-grow-1">
        <ShareOfVoiceChart
          ownBrand={ownBrand}
          competitors={competitors}
          trackedCompetitors={trackedCompetitors}
          plotHeight={plotHeight}
          focusKey={focusKey}
        />
      </Card.Body>
      {footer}
    </Card>
  );
}
